import React from 'react';
import { useParams } from 'react-router-dom';
import {
  Flex,
  Heading,
  Card,
  Box,
  DataList,
  Text,
  Grid,
  BoxProps,
} from '@radix-ui/themes';

import { useGetTransactionQuery } from '../../graphql/queries/GetTransaction.generated';
import TimeAgoTooltip from '../../components/TimeAgoTooltip';
import HashShortener from '../../components/HashShortener';
import BlockNumberDisplay from '../../components/BlockNumberDisplay';
import TransactionStatusBadge from '../../components/TransactionStatusBadge';
import { TransactionStatus, Transaction } from '../../types';

const TransactionMetadataItem = ({
  label,
  children,
  ...otherProps
}: {
  label: string;
  children: React.ReactNode;
  otherProps?: DataList.ItemProps;
}) => (
  <DataList.Item align="center" {...otherProps}>
    <DataList.Label>{label}</DataList.Label>
    <DataList.Value>{children}</DataList.Value>
  </DataList.Item>
);

const TransactionMetadataCard = ({
  title,
  children,
  ...otherProps
}: {
  title: string;
  children: React.ReactNode;
  otherProps?: BoxProps;
}) => (
  <Card size="3" asChild>
    <Box height="100%" {...otherProps}>
      <Flex align="center" justify="between" mb="5">
        <Heading as="h3" size="4" trim="both">
          {title}
        </Heading>
      </Flex>
      <DataList.Root>{children}</DataList.Root>
    </Box>
  </Card>
);

interface TransactionDetailCardProps {
  transaction: Transaction;
}

const TransactionDetailCard: React.FC<
  TransactionDetailCardProps & BoxProps
> = ({ transaction, ...otherProps }) => (
  <TransactionMetadataCard title="Details" {...otherProps}>
    <TransactionMetadataItem label="Hash">
      <HashShortener hash={transaction.hash} />
    </TransactionMetadataItem>
    <TransactionMetadataItem label="From">
      <HashShortener hash={transaction.from.address} />
    </TransactionMetadataItem>
    <TransactionMetadataItem label="To">
      {transaction.to ? (
        <HashShortener hash={transaction.to.address} />
      ) : (
        <Text>Contract Creation</Text>
      )}
    </TransactionMetadataItem>
    <TransactionMetadataItem label="Value">
      <Text>{transaction.valueEth} ETH</Text>
    </TransactionMetadataItem>
    <TransactionMetadataItem label="Gas">
      <Text>{transaction.gas}</Text>
    </TransactionMetadataItem>
    {transaction.gasPrice && (
      <TransactionMetadataItem label="Gas Price">
        <Text>{transaction.gasPrice} Gwei</Text>
      </TransactionMetadataItem>
    )}
    {transaction.maxFeePerGas && (
      <TransactionMetadataItem label="Max Fee Per Gas">
        <Text>{transaction.maxFeePerGas} Gwei</Text>
      </TransactionMetadataItem>
    )}
    {transaction.maxPriorityFeePerGas && (
      <TransactionMetadataItem label="Max Priority Fee Per Gas">
        <Text>{transaction.maxPriorityFeePerGas} Gwei</Text>
      </TransactionMetadataItem>
    )}
    {transaction.blockNumber && (
      <TransactionMetadataItem label="Block">
        <BlockNumberDisplay
          blockNumber={transaction.blockNumber}
          link={`/blocks/${transaction.blockNumber}`}
        />
      </TransactionMetadataItem>
    )}
    {transaction.blockHash && (
      <TransactionMetadataItem label="Block Hash">
        <HashShortener
          hash={transaction.blockHash}
          link={`/blocks/${transaction.blockHash}`}
        />
      </TransactionMetadataItem>
    )}
    <TransactionMetadataItem label="Timestamp">
      <TimeAgoTooltip value={transaction.createdAt} />
    </TransactionMetadataItem>
  </TransactionMetadataCard>
);

interface TransactionStatusCardProps {
  transaction: {
    status: TransactionStatus;
  };
}

const TransactionStatusCard: React.FC<
  TransactionStatusCardProps & BoxProps
> = ({ transaction, ...otherProps }) => (
  <TransactionMetadataCard title="Status" {...otherProps}>
    <TransactionMetadataItem label="Status">
      <TransactionStatusBadge status={transaction.status} />
    </TransactionMetadataItem>
  </TransactionMetadataCard>
);

const TransactionDetailView = () => {
  const { hash } = useParams();
  const { data, loading, error } = useGetTransactionQuery({
    variables: {
      hash: hash || '',
    },
  });

  if (loading) return <p>Loading...</p>;
  if (error || !data) return <p>Error :( {error?.message}</p>;

  if (!data.transaction) {
    return (
      <Flex align="center" justify="center" direction="column" gap="4">
        <Text>Not Found</Text>
      </Flex>
    );
  }

  return (
    <>
      <Flex align="center" justify="between" m="3">
        <Heading as="h2" size="6" trim="both">
          Transaction {data.transaction.hash.slice(0, 8)}...
          {data.transaction.hash.slice(-6)}
        </Heading>
      </Flex>
      <Flex gap="4" m="2" direction="column">
        <Grid columns="2" flexShrink="0" gap="4">
          <TransactionDetailCard transaction={data.transaction} />
          <TransactionStatusCard transaction={data.transaction} />
        </Grid>
      </Flex>
    </>
  );
};

export default TransactionDetailView;
